import React, { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { IScannerControls } from '@zxing/browser';

interface CameraScannerProps {
  /** Cada código decodificado pela câmera (bruto — dedup/anti-repetição fica com o chamador,
      ScanBar, que também conhece o estado da bipagem). */
  onCodigo: (codigo: string) => void;
  /**
   * Ajusta o decoder ao que se está lendo:
   * - `'produto'` (default) — bipagem contínua de EAN: config rápida, formato livre. Códigos
   *   curtos (~95 módulos) decodificam bem já na resolução default.
   * - `'chave'` — chave de acesso da NF-e: Code128 de 44 dígitos (DANFE) ou QR (NFC-e). O
   *   código é COMPRIDO (~450 módulos): na resolução default cada barra fica ~1px e raramente
   *   decodifica. Restringe os formatos aos da chave, liga TRY_HARDER e pede resolução alta.
   */
  perfil?: 'produto' | 'chave';
  /** Preview mais baixo pro celular (o workspace mostra o card de última leitura logo abaixo). */
  compact?: boolean;
}

type Estado =
  | { fase: 'carregando' }
  | { fase: 'lendo' }
  | { fase: 'erro'; mensagem: string };

/** getUserMedia dá erros com `name` conhecido — traduz pro que o operador da doca resolve. */
function mensagemErro(e: unknown): string {
  const nome = (e as { name?: string })?.name;
  switch (nome) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Permissão da câmera negada. Libere o acesso à câmera nas configurações do navegador e tente de novo.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'Nenhuma câmera encontrada neste aparelho.';
    case 'NotReadableError':
      return 'A câmera está em uso por outro app. Feche-o e tente de novo.';
    default:
      return 'Não foi possível abrir a câmera. Use o leitor USB ou digite o código.';
  }
}

/**
 * Transforma a câmera do aparelho num leitor de código de barras (modo contínuo). Renderiza o
 * preview e emite cada código decodificado via <c>onCodigo</c>. O @zxing/browser é importado
 * dinamicamente — a lib (algumas centenas de KB) só entra no bundle quando o operador liga a
 * câmera, não no carregamento da tela.
 *
 * <p>Prefere a câmera traseira (<c>facingMode: environment</c>). O ciclo de vida é 100% no
 * effect: abre ao montar, para (solta o stream/luz da câmera) ao desmontar — a ScanBar monta
 * este componente só enquanto o modo câmera está ligado.</p>
 */
const CameraScanner: React.FC<CameraScannerProps> = ({ onCodigo, perfil = 'produto', compact = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });

  // onCodigo num ref: o callback do zxing é registrado UMA vez (no start); sem o ref, cada
  // render da ScanBar recriaria onCodigo e, se estivesse na dep list do effect, reabriria a
  // câmera a cada bipe (piscando o preview e perdendo leituras).
  const onCodigoRef = useRef(onCodigo);
  useEffect(() => { onCodigoRef.current = onCodigo; }, [onCodigo]);

  useEffect(() => {
    let controls: IScannerControls | undefined;
    let cancelado = false;

    // Câmera AO VIVO exige contexto seguro (HTTPS ou localhost). Aberto pelo IP/rede local em
    // http://, o navegador do Android bloqueia o getUserMedia inteiro — e como o "Tirar foto"
    // (input capture) delega pra câmera NATIVA e continua funcionando, o operador acha que é
    // permissão negada. Detecta antes de chamar o zxing pra dar a instrução certa (usar o
    // endereço https) em vez do genérico "não foi possível abrir a câmera".
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setEstado({
        fase: 'erro',
        mensagem:
          'A câmera ao vivo exige uma conexão segura (HTTPS). Abra o hub pelo endereço https:// — pela rede/IP local (http) o navegador bloqueia a câmera. Você ainda pode usar o leitor USB, digitar o código, ou anexar imagem por "Tirar foto".',
      });
      return;
    }

    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        if (cancelado || !videoRef.current) return;

        // Perfil 'chave': o Code128 de 44 dígitos do DANFE (ou o QR da NFC-e) é comprido e só
        // decodifica com TRY_HARDER + resolução alta. Restringir os formatos aos da chave tira
        // a ambiguidade e deixa o leitor 1D focar. 'produto' fica no default rápido (EAN curto).
        let hints: Map<number, unknown> | undefined;
        if (perfil === 'chave') {
          const { DecodeHintType, BarcodeFormat } = await import('@zxing/library');
          hints = new Map<number, unknown>();
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE]);
          hints.set(DecodeHintType.TRY_HARDER, true);
        }

        // `width/height: ideal` nunca dá OverconstrainedError — o navegador aproxima da maior
        // que a câmera suporta. Em 640p cada barra da chave fica ~1px; em 1080p, ~3px.
        const video: MediaTrackConstraints =
          perfil === 'chave'
            ? { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
            : { facingMode: 'environment' };

        const reader = new BrowserMultiFormatReader(hints);
        controls = await reader.decodeFromConstraints(
          { video },
          videoRef.current,
          (result) => {
            if (result) onCodigoRef.current(result.getText().trim());
          }
        );

        // Se o componente desmontou enquanto o getUserMedia estava pendente, o controls chega
        // depois do cleanup — para na hora pra não deixar a câmera ligada.
        if (cancelado) { controls.stop(); return; }
        setEstado({ fase: 'lendo' });
      } catch (e) {
        if (!cancelado) setEstado({ fase: 'erro', mensagem: mensagemErro(e) });
      }
    })();

    return () => {
      cancelado = true;
      controls?.stop();
    };
  }, [perfil]);

  if (estado.fase === 'erro') {
    return (
      <div className="flex items-center gap-2.5 rounded-iris border border-warning-border bg-warning-soft p-3 text-sm text-warning">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
        <span>{estado.mensagem}</span>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-iris bg-black">
      {/* `playsInline` é obrigatório no iOS — sem ele o vídeo abre em tela cheia e o preview some. */}
      <video
        ref={videoRef}
        className={`mx-auto w-full object-contain ${compact ? 'max-h-[18vh]' : 'max-h-[26vh]'}`}
        autoPlay
        playsInline
        muted
      />

      {/* Mira: retângulo + linha de leitura, pra o operador saber onde encostar o código. */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="h-20 w-3/4 max-w-xs rounded-iris-sm border-2 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
      </div>

      {estado.fase === 'carregando' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-sm text-white">
          Abrindo a câmera…
        </div>
      )}

      <div className="pointer-events-none absolute bottom-2 left-0 right-0 text-center text-[11px] font-medium text-white/80">
        Aponte o código de barras para a área marcada
      </div>
    </div>
  );
};

export default CameraScanner;
